import { brainpoolP256r1 } from '@noble/curves/misc.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { DocSignature, ParsedDocument, SignatureVerifier } from '../core/types';

/**
 * One SERPRO signing certificate's EC public key, plus the metadata SERPRO
 * publishes alongside it so a {@link CertProvider} can narrow the candidate set
 * (e.g. drop expired certs, or match by issuing group). Only the public key is
 * required for verification; the rest is advisory.
 */
export interface SerproCertKey {
  /** Opaque certificate id (echoed in the verified detail for traceability). */
  id?: string;
  /**
   * The uncompressed EC public point `04 || X || Y` (65 bytes) on the
   * BrainpoolP256r1 curve. Provide it as raw bytes or lowercase hex. Helpers
   * are exported to derive this from an X.509 SPKI key (see {@link ecPointFromSpki}).
   */
  publicKey: Uint8Array | string;
  /** Optional validity window; verifiers may skip a cert outside it. */
  valid?: { from?: string; until?: string };
  /** Optional issuing-group id, for callers that select by group. */
  groupId?: string;
}

/**
 * Supplies the trusted SERPRO certificates out-of-band (the QR doesn't carry
 * them). Analogous to the CIN {@link import('./cin-verifier').KeyProvider}.
 * Implementations may pin certs in memory, read them from storage, or fetch the
 * live SERPRO endpoint — keeping that I/O outside the env-agnostic core.
 */
export interface CertProvider {
  /** The candidate SERPRO certificates to verify against, or `[]` if none. */
  serproCerts(): Promise<SerproCertKey[]>;
}

/** Cert provider backed by a fixed in-memory list (or a lazy getter). */
export class StaticCertProvider implements CertProvider {
  constructor(
    private readonly certs:
      | SerproCertKey[]
      | (() => SerproCertKey[] | Promise<SerproCertKey[]>),
  ) {}
  async serproCerts(): Promise<SerproCertKey[]> {
    return typeof this.certs === 'function' ? this.certs() : this.certs;
  }
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return out;
}

/**
 * Extracts the uncompressed EC point (`04 || X || Y`, 65 bytes) from an X.509
 * SubjectPublicKeyInfo (SPKI) key — the form SERPRO publishes its certificates
 * in. For BrainpoolP256r1 the point is the trailing 65 bytes of the structure.
 * Returns `null` if the bytes don't end in a well-formed uncompressed point.
 */
export function ecPointFromSpki(spki: Uint8Array): Uint8Array | null {
  if (spki.length < 65) {
    return null;
  }
  const point = spki.subarray(spki.length - 65);
  return point[0] === 0x04 ? point : null;
}

function toPoint(publicKey: Uint8Array | string): Uint8Array {
  return typeof publicKey === 'string' ? hexToBytes(publicKey) : publicKey;
}

function inValidityWindow(valid: SerproCertKey['valid'], now: Date): boolean {
  if (!valid) {
    return true;
  }
  if (valid.from && now < new Date(valid.from)) {
    return false;
  }
  if (valid.until && now > new Date(valid.until)) {
    return false;
  }
  return true;
}

/**
 * Verifies a CNH-e's ECDSA signature with {@link brainpoolP256r1} — the curve
 * WebCrypto lacks, so we use `@noble/curves` (runs in browser + Node). The
 * signature is DER-encoded SHA256withECDSA over the reconstructed payload
 * (`signedData`); we hash it with SHA-256 and check each candidate SERPRO cert.
 */
export class CnhSignatureVerifier implements SignatureVerifier {
  readonly id = 'br-cnh-ecdsa-brainpoolp256r1';

  constructor(private readonly certs: CertProvider) {}

  supports(doc: ParsedDocument): boolean {
    return doc.kind === 'cnh' && !!doc.signature?.signedData;
  }

  async verify(doc: ParsedDocument): Promise<DocSignature> {
    const sig = doc.signature!;
    const certs = await this.certs.serproCerts();
    // Diagnostics threaded onto every returned signature so an agent can tell WHY a
    // genuine CNH-e came back unverified/invalid (no certs vs no match vs which certs
    // were tried). Cert ids are opaque UUIDs — no PII.
    const candidateCertCount = certs.length;
    const triedCertIds: string[] = [];
    if (!certs.length) {
      return {
        ...sig,
        status: 'unverified',
        detail: 'No SERPRO certificate provided.',
        verifierId: this.id,
        candidateCertCount,
        triedCertIds,
      };
    }
    try {
      const digest = sha256(sig.signedData!);
      const derSig = hexToBytes(sig.hex);
      const now = new Date();
      for (const cert of certs) {
        if (!inValidityWindow(cert.valid, now)) {
          continue;
        }
        const point = toPoint(cert.publicKey);
        if (point.length !== 65 || point[0] !== 0x04) {
          continue; // not an uncompressed BrainpoolP256r1 point
        }
        if (cert.id) triedCertIds.push(cert.id);
        let ok = false;
        try {
          // `lowS: false` is REQUIRED: SERPRO signs with non-canonical (high-S) ECDSA
          // signatures, which OpenSSL accepts but @noble rejects by default (lowS: true) —
          // without this, every genuine CNH-e fails as 'invalid'. Verified against a real
          // CNH-e + live SERPRO certs (see reader-cnh reference impl).
          ok = brainpoolP256r1.verify(derSig, digest, point, { prehash: false, format: 'der', lowS: false });
        } catch {
          continue; // cert key incompatible — try the next one
        }
        if (ok) {
          return {
            ...sig,
            status: 'verified',
            detail: cert.id
              ? `Signed by SERPRO (cert ${cert.id}).`
              : 'Signed by SERPRO (ECDSA BrainpoolP256r1).',
            verifierId: this.id,
            candidateCertCount,
            triedCertIds,
            matchedCertId: cert.id,
          };
        }
      }
      return {
        ...sig,
        status: 'invalid',
        detail: 'Signature did not match any SERPRO certificate — possible forgery.',
        verifierId: this.id,
        candidateCertCount,
        triedCertIds,
      };
    } catch (e) {
      return {
        ...sig,
        status: 'unverified',
        detail: `Verification error: ${String(e)}`,
        verifierId: this.id,
        candidateCertCount,
        triedCertIds,
      };
    }
  }
}
